import {
  Checkbox,
  Dialog,
  Flex,
  Slider,
  Text,
  TextArea,
  TextField,
} from "@radix-ui/themes";
import React, { useEffect, useState } from "react";

import { Button } from "@mui/material";
import ComboBox from "./ComboBox";
import InfoIcon from "@mui/icons-material/Info";
import LoadingButton from "@mui/lab/LoadingButton";
import { Tooltip } from "@mui/joy";
import confetti from "canvas-confetti";
import { enqueueSnackbar } from "notistack";
import { motion } from "framer-motion";
import { nanoid } from "@reduxjs/toolkit";
import { setExerciseData } from "../features/exerciseDataReducer";
import { setUserIdStore } from "../features/userSlice";
import { supabase } from "../utils/supabase";
import { useDispatch } from "react-redux";

const DialogInput = ({
  selectedBodyPart,
  exerciseData,
  setSelectedBodyPart,
  isLoading,
  customExercises,
  setCustomExercises,
}) => {
  const [exerciseName, setExerciseName] = useState("");
  const [tempData, setTempData] = useState({
    weight: "",
    reps: "",
    note: "",
    rpe: "",
  });

  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const cachedData = JSON.parse(localStorage.getItem("exerciseData")) || [];
  const [noteChecked, setNoteChecked] = useState(false);
  const [rpeChecked, setRpeChecked] = useState(false);
  const [user_id, setUserId] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [fetchedData, setFetchedData] = useState([]);
  const dispatch = useDispatch();
  const isBodyWeight = selectedBodyPart === "bodyweight";

  const selectedBodyPartStorage =
    localStorage.getItem("selectedBodyPart") || "";
  const exerciseDataStorage = JSON.parse(
    localStorage.getItem("currentDayData")
  );

  useEffect(() => {
    const fetchUserId = async () => {
      try {
        const fetchedUserId = localStorage.getItem("userId" || null);
        setUserId(fetchedUserId);
      } catch (error) {
        console.error("Error fetching user session:", error);
      }
    };

    fetchUserId();
  }, []);

  useEffect(() => {
    dispatch(setUserIdStore(user_id));
    if (
      user_id !== undefined &&
      user_id !== "3cdf8cc0-6f83-4831-98a1-35b36dcd773d"
    ) {
      const fetchDataFromSupabase = async () => {
        try {
          const { data, error } = await supabase
            .from("exercise_data")
            .select("*")
            .eq("user_id", user_id);
          if (error) {
            throw error;
          }

          // Use the data from Supabase

          setFetchedData(data || []); // If data is null, set an empty array
        } catch (error) {
          console.error("Error fetching data from Supabase:", error);
        }
      };

      if (user_id !== null) {
        fetchDataFromSupabase();
      }

      if (selectedBodyPartStorage) {
        setSelectedBodyPart(selectedBodyPartStorage);
      }
    }
  }, [user_id]);
  useEffect(() => {
    if (fetchedData) {
      const sortedData = fetchedData;
      dispatch(setExerciseData(sortedData));

      localStorage.setItem("currentDayData", JSON.stringify(sortedData));
    }

    if (user_id) {
      syncDataWithSupabase();
    }
  }, [fetchedData]);

  useEffect(() => {
    if (exerciseData.length > 0 || cachedData.length > 0) {
      setIsButtonDisabled(false);
    }
  }, [exerciseData, cachedData]);

  function deepEqual(obj1, obj2) {
    if (obj1 === obj2) {
      return true;
    }

    if (
      typeof obj1 !== "object" ||
      obj1 === null ||
      typeof obj2 !== "object" ||
      obj2 === null
    ) {
      return false;
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    for (const key of keys1) {
      if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
        return false;
      }
    }

    return true;
  }
  async function syncDataWithSupabase() {
    if (user_id !== "3cdf8cc0-6f83-4831-98a1-35b36dcd773d") {
      try {
        const { data: supabaseData, error } = await supabase
          .from("exercise_data")
          .select("*")
          .eq("user_id", user_id);

        if (error) {
          throw error;
        }
        const localData =
          JSON.parse(localStorage.getItem("currentDayData")) || [];
        const newData = localData?.filter(
          (localItem) =>
            localItem.user_id === user_id &&
            !fetchedData.some(
              (supabaseItem) => supabaseItem.id === localItem.id
            )
        );
        if (newData?.length > 0) {
          await supabase.from("exercise_data").upsert(newData);
        }

        // Identify edited data (those with changes)
        const dataToChange = localData.filter((localItem) =>
          supabaseData.some(
            (supabaseItem) =>
              supabaseItem.id === localItem.id &&
              !deepEqual(supabaseItem, localItem)
          )
        );

        // Batch upsert new data
        if (dataToChange.length > 0) {
          await supabase.from("exercise_data").upsert(dataToChange);
        }

        // Optionally, update local storage after synchronization
        localStorage.setItem("currentDayData", JSON.stringify(localData));
      } catch (error) {
        console.error("Error syncing data", error);
        enqueueSnackbar("Failed to sync data. Please try again.", {
          variant: "error",
        });
      }
    }
  }

  useEffect(() => {
    // Check if the new data is different from the current state
    if (JSON.stringify(exerciseDataStorage) !== JSON.stringify(fetchedData)) {
      setFetchedData(exerciseDataStorage);
    }
  }, [exerciseDataStorage, fetchedData]);

  useEffect(() => {
    // Ensure selectedBodyPart is truthy before comparing
    if (isBodyWeight) {
      return;
    }
    if (selectedBodyPart !== selectedBodyPartStorage) {
      if (selectedBodyPart) {
        localStorage.setItem("selectedBodyPart", selectedBodyPart);
      }
    }
  }, [selectedBodyPart, selectedBodyPartStorage]);

  useEffect(() => {
    if (exerciseData.length > 0 || cachedData.length > 0) {
      setIsButtonDisabled(false);
    }
  }, [exerciseData, cachedData]);

  function calculateOneRM(exercise) {
    let adjustedReps = parseFloat(exercise.reps);
    const exerciseWeight = parseFloat(exercise.weight);
    const rpeAdjustment = exercise.rpe ? 10 - parseFloat(exercise.rpe) : 0;
    adjustedReps += rpeAdjustment;

    const oneRMWeight = (
      exerciseWeight /
      (1.0278 - 0.0278 * adjustedReps)
    ).toFixed(1);

    return parseFloat(oneRMWeight);
  }

  function isPR(exercise) {
    const sameExerciseData = exerciseDataStorage.filter(
      (data) => data.exerciseName === exerciseName
    );

    const isRMGreater = (element) =>
      calculateOneRM(element) < calculateOneRM(exercise);
    return sameExerciseData.every(isRMGreater);
  }
  function triggerConfetti() {
    confetti({
      particleCount: 100,
      origin: { y: 0.9 },
      spread: 50,
    });
  }

  async function handleSave(e) {
    const isBodyweight = selectedBodyPart === "bodyweight";
    const validNumber = /^[0-9]+(\.[0-9]+)?$/;
    const validRPE = /^[1-10]$/;
    if (isBodyweight) {
      const isBodyweightValid =
        validNumber.test(tempData.bodyweight) && tempData.bodyweight !== "";

      if (!isBodyweightValid) {
        e.preventDefault();
        document.getElementsByName("bodyweight")[0].classList.add("error");
        enqueueSnackbar("Correct the highlighted fields", {
          variant: "error",
        });
      } else {
        setFetchedData((prevFetchedData) => [
          ...prevFetchedData,
          {
            user_id: user_id,
            id: nanoid(),
            exerciseName: "Bodyweight",
            bodypart: "bodyweight",
            date: new Date(),
            weight: parseFloat(tempData.bodyweight),
            note: tempData.note,
          },
        ]);

        enqueueSnackbar("Bodyweight added!", {
          variant: "success",
        });

        // Clear the tempData state after saving
        setTempData({ bodyweight: "", note: "" });
        setNoteChecked(false);
        setIsOpen(false);
      }
    } else {
      const exerciseInput =
        document.getElementsByClassName("combobox")[0].value;

      // Check if exerciseInput doesn't consist solely of numeric characters
      const isExerciseInputValid = !/^\d+$/.test(exerciseInput);
      // Check if the numeric values for weight and reps are valid
      const isWeightValid = validNumber.test(tempData.weight);
      const isRepsValid = validNumber.test(tempData.reps);
      const isRpeValid = validRPE.test(tempData.rpe);
      const isExistingExercise = exerciseDataStorage.some(
        (data) => data.exerciseName === exerciseName
      );
      const noteValue = [tempData.note, tempData.rpe && `RPE: ${tempData.rpe}`]
        .filter(Boolean)
        .join(", ");

      if (
        !exerciseInput ||
        !isExerciseInputValid ||
        !isWeightValid ||
        !isRepsValid ||
        (rpeChecked && !tempData.rpe) ||
        (noteChecked && !tempData.note)
      ) {
        enqueueSnackbar("Correct the highlighted fields", {
          variant: "error",
        });

        if (!exerciseInput || !isExerciseInputValid) {
          document.getElementsByClassName("combobox")[0].classList.add("error");
        }

        if (!isWeightValid) {
          document.getElementsByName("weight")[0].classList.add("error");
        }

        if (!isRepsValid) {
          document.getElementsByName("reps")[0].classList.add("error");
        }
        if (!isRpeValid) {
          document.getElementsByName("rpe")[0].classList.add("error");
        }

        if (noteChecked && !tempData.note) {
          document.getElementsByName("note")[0].classList.add("error");
        }

        e.preventDefault();
        return;
      } else {
        setFetchedData((prevFetchedData) => [
          ...prevFetchedData,
          {
            user_id: user_id,
            id: nanoid(),
            exerciseName: exerciseName.replace(/"/g, ""),
            bodypart: selectedBodyPartStorage,
            date: new Date(),
            reps: parseFloat(tempData.reps),
            weight: parseFloat(tempData.weight),
            rpe: parseFloat(tempData.rpe),
            note: noteValue,
          },
        ]);
        if (isPR(tempData) && isExistingExercise) {
          console.log("PR");
          enqueueSnackbar("Congratulations! You have hit a PR!", {
            variant: "success",
            autoHideDuration: 5000,
          });
          triggerConfetti();
        }
        console.log("exercise name", exerciseName);
        enqueueSnackbar("Exercise added!", {
          variant: "success",
        });

        // Clear the tempData state after saving
        setTempData({ reps: "", weight: "", note: "" });
        setNoteChecked(false);
        setIsOpen(false);
      }
    }
  }
  function onChange(event) {
    setExerciseName(localStorage.getItem("searchValue"));
    const { name, value } = event.target;

    // Check if the input is a valid number
    if (name !== "note") {
      const validNumber = /^[0-9]+(\.[0-9]*)?$/.test(value);
      const validRPE = /^(?:[1-9](?:\.\d{0,2})?|10(?:\.0{1,2})?)$/.test(value);

      if (name === "rpe" && !validRPE && value !== "") {
        enqueueSnackbar(`Only RPE values between 1 and 10 are accepted!`, {
          variant: "warning",
        });
        document.getElementsByName(name)[0].classList.add("error");
        return;
      }
      if (!validNumber && value !== "") {
        enqueueSnackbar(`Only numbers are accepted for the ${name} field!`, {
          variant: "warning",
        });

        // Highlight the input as an error
        document.getElementsByName(name)[0].classList.add("error");
        return;
      } else {
        // Remove the error highlighting if the input is valid
        document.getElementsByName(name)[0].classList.remove("error");
      }
    }

    const comboboxElement = document.getElementsByClassName("combobox")[0];

    if (comboboxElement && exerciseName !== "") {
      comboboxElement.classList.remove("error");
    }

    setTempData((prevTempData) => ({
      ...prevTempData,
      [name]: value,
    }));
  }
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      const nextInput = document.querySelector(
        `[tabIndex="${e.target.tabIndex + 1}"]`
      );
      if (nextInput) {
        nextInput.focus();
      }
    }
  };

  return (
    <Dialog.Root open={isOpen}>
      <Dialog.Trigger>
        <LoadingButton
          onClick={() => setIsOpen(true)}
          disabled={isButtonDisabled}
          color="primary"
          variant="outlined"
          loading={isLoading}
          style={{
            cursor: "pointer",
            margin: "auto 0",
            minWidth: "10rem",
            alignSelf: "center",
          }}
        >
          {isButtonDisabled
            ? "Select a bodypart"
            : isBodyWeight
            ? "Add bodyweight"
            : "Add exercise"}
        </LoadingButton>
      </Dialog.Trigger>

      <Dialog.Content
        onPointerDownOutside={() => setIsOpen(false)}
        style={{
          maxWidth: 450,
          alignItems: "center",
          height: !isBodyWeight
            ? noteChecked && rpeChecked
              ? 480
              : noteChecked
              ? 440
              : rpeChecked
              ? 410
              : 370
            : noteChecked
            ? 300
            : 230,
          transition: "height 0.2s ease",
          overflowY: "hidden",
        }}
      >
        <Dialog.Title>
          {isBodyWeight
            ? "Add Bodyweight"
            : `Add Exercise for ${
                selectedBodyPart.slice(0, 1).toUpperCase() +
                selectedBodyPart.slice(1)
              }`}
        </Dialog.Title>
        <Dialog.Description size="2" mb="4">
          {!isBodyWeight
            ? "Select the exercise and add details of your top set"
            : "Add your current bodyweight"}
        </Dialog.Description>

        <Flex direction="column" gap="3">
          {isBodyWeight ? (
            <>
              <label>
                <TextField.Input
                  onKeyDown={(e) => e.key === "Enter" && handleSave()}
                  placeholder="Bodyweight"
                  name="bodyweight" // Set a specific name for bodyweight input
                  onChange={onChange}
                  value={tempData.bodyweight}
                  inputMode="numeric"
                />
              </label>
              <Text as="label">
                <Flex gap="1" align="center" style={{ width: "fit-content" }}>
                  <Checkbox
                    style={{ boxShadow: "inset 0 0 0 1px" }}
                    highContrast
                    variant="outline"
                    checked={noteChecked}
                    onCheckedChange={() => setNoteChecked(!noteChecked)}
                    name="note"
                    id="note"
                  />
                  <span style={{ fontSize: "14px", fontWeight: "500" }}>
                    Add a note?
                  </span>
                </Flex>
              </Text>
              {noteChecked && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <TextArea
                    onKeyDown={(e) => e.key === "Enter" && handleSave()}
                    placeholder="Note.."
                    name="note"
                    autoComplete
                    onChange={onChange}
                    value={tempData.note}
                  />
                </motion.div>
              )}
            </>
          ) : (
            <>
              <ComboBox
                selectedBodyPart={selectedBodyPart}
                customExercises={customExercises}
                setCustomExercises={setCustomExercises}
                exerciseDataStorage={exerciseDataStorage}
                handleKeyPress={handleKeyPress}
              />
              <label>
                <TextField.Input
                  placeholder="Weight"
                  name="weight"
                  onChange={onChange}
                  value={tempData.weight}
                  inputMode="numeric"
                  tabIndex="2"
                  onKeyDown={(e) => handleKeyPress(e)}
                />
              </label>
              <label>
                <TextField.Input
                  onKeyDown={(e) => e.key === "Enter" && handleSave()}
                  placeholder="Reps"
                  name="reps"
                  onChange={onChange}
                  value={tempData.reps}
                  inputMode="numeric"
                  tabIndex="3"
                />
              </label>
              <Slider
                variant="soft"
                min={1}
                max={30}
                step={1}
                defaultValue={1}
                value={[tempData.reps]}
                onValueChange={(value) =>
                  setTempData((prevData) => ({
                    ...prevData,
                    reps: value[0],
                  }))
                }
              />
              <Flex gap="1" align="center">
                <Text as="label" className="flex items-center gap-1">
                  <Checkbox
                    style={{ boxShadow: "inset 0 0 0 1px" }}
                    highContrast
                    variant="outline"
                    checked={rpeChecked}
                    onCheckedChange={() => {
                      setRpeChecked(!rpeChecked);
                      if (!rpeChecked) setTempData({ ...tempData, rpe: "" });
                    }}
                    name="rpe"
                    id="rpe"
                  />
                  <span style={{ fontSize: "14px", fontWeight: "500" }}>
                    Add RPE?{" "}
                  </span>
                </Text>
                <Tooltip
                  leaveTouchDelay={4000}
                  arrow
                  variant="outlined"
                  title="RPE (Rating of Perceived Exertion) is a 1-10 scale measuring exercise intensity. 1 = could have done 9 more reps, 10 = could have done 0 more reps."
                >
                  <InfoIcon />
                </Tooltip>
              </Flex>

              {rpeChecked && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <TextField.Input
                    onChange={onChange}
                    value={tempData.rpe}
                    placeholder="Enter RPE (1-10)"
                    name="rpe"
                    id="rpe"
                    inputMode="numeric"
                    tabIndex="4"
                    onKeyDown={(e) => handleKeyPress(e)}
                  />
                </motion.div>
              )}
              <Text as="label" style={{ width: "fit-content" }}>
                <Flex gap="1" align="center" style={{ width: "fit-content" }}>
                  <Checkbox
                    style={{ boxShadow: "inset 0 0 0 1px" }}
                    highContrast
                    variant="outline"
                    checked={noteChecked}
                    onCheckedChange={() => setNoteChecked(!noteChecked)}
                    name="note"
                    id="note"
                  />
                  <span style={{ fontSize: "14px", fontWeight: "500" }}>
                    Add a note?
                  </span>
                </Flex>
              </Text>
              {noteChecked && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <TextArea
                    placeholder="Note.."
                    name="note"
                    onChange={onChange}
                    value={tempData.note}
                    tabIndex="5"
                  />
                </motion.div>
              )}
            </>
          )}
        </Flex>

        <Flex gap="3" justify="end" style={{ marginTop: 10 }}>
          <Dialog.Close>
            <Button
              variant="soft"
              color="gray"
              onClick={() => {
                setNoteChecked(false);
                setTempData({ weight: "", reps: "", note: "" });
                setIsOpen(false);
              }}
            >
              Cancel
            </Button>
          </Dialog.Close>
          <Dialog.Close>
            <Button color="primary" onClick={handleSave}>
              Save
            </Button>
          </Dialog.Close>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default DialogInput;
